import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  Badge
} from '@mui/material';
import {
  CloudOff as CloudOffIcon,
  Cloud as CloudIcon,
  Sync as SyncIcon,
  SyncProblem as SyncProblemIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  DevicesOther as DeviceIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Merge as MergeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`offline-tabpanel-${index}`}
      aria-labelledby={`offline-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

interface SyncStatus {
  syncRecord: any;
  pendingOperations: number;
  conflicts: number;
  activeSnapshot: any;
  lastSync: string;
  isOfflineCapable: boolean;
}

interface OfflineDevice {
  deviceId: string;
  locationId: string;
  status: SyncStatus;
  lastSeen: Date;
}

interface ConflictItem {
  _id: string;
  conflictType: string;
  modelName: string;
  documentId: string;
  clientData: any;
  serverData: any;
  resolution: any;
  createdAt: Date;
}

const OfflineModePage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<OfflineDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [conflicts, setConflicts] = useState<ConflictItem[]>([]);
  const [queueItems, setQueueItems] = useState<any[]>([]);
  const [issyncing, setSyncing] = useState(false);
  const [conflictDialog, setConflictDialog] = useState<{
    open: boolean;
    conflict: ConflictItem | null;
  }>({ open: false, conflict: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [autoSync, setAutoSync] = useState(true);

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(() => {
      if (autoSync) {
        fetchDevices();
      }
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [autoSync]);

  useEffect(() => {
    if (selectedDevice) {
      fetchDeviceDetails(selectedDevice);
    }
  }, [selectedDevice]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      // In a real implementation, this would fetch all devices with offline capability
      // For demo, we'll simulate with mock data
      const mockDevices: OfflineDevice[] = [
        {
          deviceId: 'DEVICE-001',
          locationId: 'LOC-001',
          status: {
            syncRecord: { status: 'completed' },
            pendingOperations: 0,
            conflicts: 0,
            activeSnapshot: {
              id: 'SNAP-001',
              type: 'full',
              validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
              size: 2048576
            },
            lastSync: new Date().toISOString(),
            isOfflineCapable: true
          },
          lastSeen: new Date()
        },
        {
          deviceId: 'DEVICE-002',
          locationId: 'LOC-002',
          status: {
            syncRecord: { status: 'pending' },
            pendingOperations: 5,
            conflicts: 2,
            activeSnapshot: null,
            lastSync: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            isOfflineCapable: false
          },
          lastSeen: new Date(Date.now() - 60 * 60 * 1000)
        }
      ];
      setDevices(mockDevices);
      if (!selectedDevice && mockDevices.length > 0) {
        setSelectedDevice(mockDevices[0].deviceId);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceDetails = async (deviceId: string) => {
    try {
      const device = devices.find(d => d.deviceId === deviceId);
      if (device) {
        setSyncStatus(device.status);
        
        // Fetch conflicts and queue items
        if (device.status.conflicts > 0) {
          // Simulate conflict data
          const mockConflicts: ConflictItem[] = [
            {
              _id: '1',
              conflictType: 'version_mismatch',
              modelName: 'Reservation',
              documentId: 'RES-001',
              clientData: { status: 'confirmed' },
              serverData: { status: 'cancelled' },
              resolution: null,
              createdAt: new Date()
            }
          ];
          setConflicts(mockConflicts);
        }
        
        if (device.status.pendingOperations > 0) {
          // Simulate queue items
          const mockQueue = [
            {
              _id: '1',
              operationType: 'update',
              modelName: 'Payment',
              status: 'pending',
              timestamp: new Date()
            }
          ];
          setQueueItems(mockQueue);
        }
      }
    } catch (error) {
      console.error('Error fetching device details:', error);
    }
  };

  const handleSync = async (deviceId: string) => {
    try {
      setSyncing(true);
      const response = await api.post('/api/offline-sync/sync', {
        deviceId,
        locationId: devices.find(d => d.deviceId === deviceId)?.locationId
      });
      
      if (response.data.success) {
        // Refresh device status
        await fetchDevices();
      }
    } catch (error) {
      console.error('Error syncing device:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateSnapshot = async (deviceId: string, type: 'full' | 'partial') => {
    try {
      const device = devices.find(d => d.deviceId === deviceId);
      if (!device) return;
      
      const response = await api.post('/api/offline-sync/snapshot', {
        deviceId,
        locationId: device.locationId,
        snapshotType: type
      });
      
      if (response.data.success) {
        await fetchDevices();
      }
    } catch (error) {
      console.error('Error creating snapshot:', error);
    }
  };

  const handleResolveConflict = async (conflictId: string, resolution: any) => {
    try {
      const response = await api.post(`/api/offline-sync/conflicts/${conflictId}/resolve`, {
        resolution,
        data: resolution.data
      });
      
      if (response.data.success) {
        setConflictDialog({ open: false, conflict: null });
        await fetchDeviceDetails(selectedDevice!);
      }
    } catch (error) {
      console.error('Error resolving conflict:', error);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'pending':
        return <ScheduleIcon color="warning" />;
      case 'in_progress':
        return <SyncIcon color="info" className="spin" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Offline Mode Management
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={autoSync}
              onChange={(e) => setAutoSync(e.target.checked)}
              color="primary"
            />
          }
          label="Auto-sync"
        />
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Overview" icon={<CloudOffIcon />} iconPosition="start" />
          <Tab label="Devices" icon={<DeviceIcon />} iconPosition="start" />
          <Tab label="Sync Queue" icon={<SyncIcon />} iconPosition="start" />
          <Tab label="Conflicts" icon={<WarningIcon />} iconPosition="start" />
          <Tab label="Snapshots" icon={<StorageIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {/* Overview Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DeviceIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Devices</Typography>
                </Box>
                <Typography variant="h3">{devices.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {devices.filter(d => d.status.isOfflineCapable).length} offline capable
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SyncIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Pending Sync</Typography>
                </Box>
                <Typography variant="h3">
                  {devices.reduce((sum, d) => sum + d.status.pendingOperations, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Operations waiting
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WarningIcon sx={{ mr: 1 }} color="warning" />
                  <Typography variant="h6">Conflicts</Typography>
                </Box>
                <Typography variant="h3" color="warning.main">
                  {devices.reduce((sum, d) => sum + d.status.conflicts, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Need resolution
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StorageIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Active Snapshots</Typography>
                </Box>
                <Typography variant="h3">
                  {devices.filter(d => d.status.activeSnapshot).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ready for offline
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Sync Activity
                </Typography>
                <List>
                  {devices.slice(0, 5).map((device) => (
                    <ListItem key={device.deviceId}>
                      <ListItemIcon>
                        {getStatusIcon(device.status.syncRecord?.status || 'pending')}
                      </ListItemIcon>
                      <ListItemText
                        primary={`Device ${device.deviceId}`}
                        secondary={`Last sync: ${device.status.lastSync ? 
                          format(new Date(device.status.lastSync), 'MMM d, yyyy h:mm a') : 
                          'Never'}`}
                      />
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          startIcon={<SyncIcon />}
                          onClick={() => handleSync(device.deviceId)}
                          disabled={issyncing}
                        >
                          Sync Now
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Devices Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Registered Devices
              </Typography>
              <List>
                {devices.map((device) => (
                  <ListItem
                    key={device.deviceId}
                    button
                    selected={selectedDevice === device.deviceId}
                    onClick={() => setSelectedDevice(device.deviceId)}
                  >
                    <ListItemIcon>
                      <Badge
                        badgeContent={device.status.pendingOperations}
                        color="warning"
                      >
                        <DeviceIcon />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={device.deviceId}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            Location: {device.locationId}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Last seen: {format(device.lastSeen, 'MMM d, h:mm a')}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {device.status.isOfflineCapable ? (
                        <Chip
                          icon={<CloudOffIcon />}
                          label="Offline"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<CloudIcon />}
                          label="Online Only"
                          size="small"
                        />
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            {selectedDevice && syncStatus && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Device Details: {selectedDevice}
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Sync Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getStatusIcon(syncStatus.syncRecord?.status || 'pending')}
                      <Typography sx={{ ml: 1 }}>
                        {syncStatus.syncRecord?.status || 'Not initialized'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Last Sync
                    </Typography>
                    <Typography>
                      {syncStatus.lastSync ? 
                        format(new Date(syncStatus.lastSync), 'MMM d, yyyy h:mm a') : 
                        'Never'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Pending Operations
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {syncStatus.pendingOperations}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Conflicts
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {syncStatus.conflicts}
                    </Typography>
                  </Grid>
                </Grid>
                
                {syncStatus.activeSnapshot && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Offline Ready</strong> - Snapshot valid until{' '}
                      {format(new Date(syncStatus.activeSnapshot.validUntil), 'MMM d, yyyy')}
                    </Typography>
                    <Typography variant="caption">
                      Size: {formatBytes(syncStatus.activeSnapshot.size)} • 
                      Type: {syncStatus.activeSnapshot.type}
                    </Typography>
                  </Alert>
                )}
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SyncIcon />}
                    onClick={() => handleSync(selectedDevice)}
                    disabled={issyncing}
                  >
                    Sync Now
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleCreateSnapshot(selectedDevice, 'partial')}
                  >
                    Create Snapshot
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<StorageIcon />}
                    onClick={() => handleCreateSnapshot(selectedDevice, 'full')}
                  >
                    Full Backup
                  </Button>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Sync Queue Tab */}
        <Paper>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Offline Operations Queue
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Operation</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Document ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Retry Count</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queueItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>
                      {format(new Date(item.timestamp), 'MMM d, h:mm a')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.operationType}
                        size="small"
                        color={
                          item.operationType === 'create' ? 'success' :
                          item.operationType === 'update' ? 'primary' :
                          'error'
                        }
                      />
                    </TableCell>
                    <TableCell>{item.modelName}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{item.documentId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        color={
                          item.status === 'synced' ? 'success' :
                          item.status === 'pending' ? 'warning' :
                          item.status === 'conflict' ? 'error' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{item.retryCount || 0}</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <InfoIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={queueItems.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {/* Conflicts Tab */}
        <Paper>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Sync Conflicts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and resolve data conflicts between offline and online versions
            </Typography>
          </Box>
          
          <List>
            {conflicts.map((conflict) => (
              <ListItem key={conflict._id} divider>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={`${conflict.modelName} - ${conflict.documentId}`}
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">
                        Type: {conflict.conflictType}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Occurred: {format(new Date(conflict.createdAt), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => setConflictDialog({ open: true, conflict })}
                  >
                    Resolve
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          
          {conflicts.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No conflicts to resolve
              </Typography>
            </Box>
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        {/* Snapshots Tab */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Offline Data Snapshots
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage offline data packages for devices
          </Typography>
          
          <Grid container spacing={3}>
            {devices.filter(d => d.status.activeSnapshot).map((device) => (
              <Grid item xs={12} md={6} key={device.deviceId}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        {device.deviceId}
                      </Typography>
                      <Chip
                        label={device.status.activeSnapshot.type}
                        size="small"
                        color="primary"
                      />
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Size
                        </Typography>
                        <Typography>
                          {formatBytes(device.status.activeSnapshot.size)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Valid Until
                        </Typography>
                        <Typography>
                          {format(new Date(device.status.activeSnapshot.validUntil), 'MMM d, yyyy')}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          ((new Date(device.status.activeSnapshot.validUntil).getTime() - Date.now()) /
                          (7 * 24 * 60 * 60 * 1000)) * 100
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Snapshot validity
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => handleCreateSnapshot(device.deviceId, 'partial')}
                      >
                        Refresh
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        color="error"
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </TabPanel>

      {/* Conflict Resolution Dialog */}
      <Dialog
        open={conflictDialog.open}
        onClose={() => setConflictDialog({ open: false, conflict: null })}
        maxWidth="md"
        fullWidth
      >
        {conflictDialog.conflict && (
          <>
            <DialogTitle>
              Resolve Conflict - {conflictDialog.conflict.modelName}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Alert severity="warning">
                  {conflictDialog.conflict.conflictType === 'version_mismatch' &&
                    'The offline and online versions of this record have diverged.'}
                  {conflictDialog.conflict.conflictType === 'deleted_update' &&
                    'This record was deleted online but updated offline.'}
                  {conflictDialog.conflict.conflictType === 'concurrent_update' &&
                    'This record was updated both online and offline.'}
                </Alert>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Offline Version (Client)
                    </Typography>
                    <pre style={{ fontSize: '0.8rem', overflow: 'auto' }}>
                      {JSON.stringify(conflictDialog.conflict.clientData, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
                
                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Online Version (Server)
                    </Typography>
                    <pre style={{ fontSize: '0.8rem', overflow: 'auto' }}>
                      {JSON.stringify(conflictDialog.conflict.serverData, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Resolution Strategy</InputLabel>
                  <Select defaultValue="server_wins">
                    <MenuItem value="server_wins">Keep Online Version</MenuItem>
                    <MenuItem value="client_wins">Keep Offline Version</MenuItem>
                    <MenuItem value="merge">Merge Changes</MenuItem>
                    <MenuItem value="manual">Custom Resolution</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Resolution Notes"
                  placeholder="Add notes about this resolution..."
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConflictDialog({ open: false, conflict: null })}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleResolveConflict(
                  conflictDialog.conflict!._id,
                  { strategy: 'server_wins', notes: '' }
                )}
              >
                Resolve Conflict
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OfflineModePage;